// MACH-REF-I18N-1 §4.2 — the machine designation ("5-Axis CNC Milling Machine") built from
// per-language templates, not by joining English words.
//
//   CNC machine     axis + operation -> compose.cnc.{slot of the axis row}, qualifier agreeing with
//                   the operation noun's gender; operation only -> compose.cnc.noAxis; axis only ->
//                   the same templates with compose.cnc.genericNoun; neither -> the type label.
//                   Axis `other` is skipped (owner-accepted: "Other CNC Milling Machine" -> "CNC
//                   Milling Machine").
//   Production line compose.line.full / opOnly / automationOnly from lineForm + lineQualifier;
//                   neither -> the type label.
//   Other type      compose.otherTypeMachine ("Other machine").
//   Any other type  the type label alone (owner ruling Q4: "Compressor", not "Compressor Machine").
//
// NO LANGUAGE MIXING: if any piece the chosen template needs has no code or no key in the current
// language, the WHOLE name falls back to `composeEnglishLegacy` (today's English composition with
// the same two approved deltas), so a half-translated phrase is never shown.
import {
  lookupMachineRef,
  isOtherRef,
  resolveRefCode,
  MachineRef,
  Translate,
} from "./machineReferenceLabels";

export interface ComposableMachine {
  type?: MachineRef | null;
  axisConfiguration?: MachineRef | null;
  operationType?: MachineRef | null;
  automationLevel?: MachineRef | null;
}

const has = (ref?: MachineRef | null): ref is MachineRef => !!ref && !!(ref.name || ref.code);

const GENDER_SUFFIX: Record<string, string> = { f: "F", m: "M", n: "N" };

/** The template decides capitals; the first character of the final string is upper-cased (§4.1). */
export const capitalizeFirst = (text: string): string =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

const composeCnc = (m: ComposableMachine, translate: Translate): string | null => {
  const ref = (path: string, vars?: Record<string, unknown>) => lookupMachineRef(path, translate, vars);
  const axis = has(m.axisConfiguration) && !isOtherRef(m.axisConfiguration) ? m.axisConfiguration : null;
  const op = has(m.operationType) ? m.operationType : null;
  if (!axis && !op) return ref("type.cnc-machine.label");
  if ((axis && !axis.code) || (op && !op.code)) return null;

  const noun = op ? ref(`operation.${op.code}.noun`) : ref("compose.cnc.genericNoun");
  const gender = op ? ref(`operation.${op.code}.nounGender`) : ref("compose.cnc.genericNounGender");
  if (!noun || !gender || !GENDER_SUFFIX[gender]) return null;
  if (!axis) return ref("compose.cnc.noAxis", { noun });

  const slot = ref(`axis.${axis.code}.slot`);
  const qualifier = ref(`axis.${axis.code}.qualifier${GENDER_SUFFIX[gender]}`);
  if ((slot !== "prefix" && slot !== "suffix") || !qualifier) return null;
  return ref(`compose.cnc.${slot}`, { qualifier, noun });
};

const composeLine = (m: ComposableMachine, translate: Translate): string | null => {
  const ref = (path: string, vars?: Record<string, unknown>) => lookupMachineRef(path, translate, vars);
  const level = has(m.automationLevel) ? m.automationLevel : null;
  const op = has(m.operationType) ? m.operationType : null;
  if (!level && !op) return ref("type.production-line.label");
  if ((level && !level.code) || (op && !op.code)) return null;

  const automation = level ? ref(`automation.${level.code}.lineQualifier`) : null;
  const opForm = op ? ref(`operation.${op.code}.lineForm`) : null;
  if ((level && !automation) || (op && !opForm)) return null;
  const template = level && op ? "full" : op ? "opOnly" : "automationOnly";
  return ref(`compose.line.${template}`, { automation, op: opForm });
};

/** Localised composition, or null when any needed piece is missing (caller falls back to English). */
export const composeLocalized = (m: ComposableMachine, translate: Translate): string | null => {
  const typeCode = resolveRefCode("type", m.type);
  if (!typeCode) return null;
  switch (typeCode) {
    case "cnc-machine":
      return composeCnc(m, translate);
    case "production-line":
      return composeLine(m, translate);
    case "other":
      return lookupMachineRef("compose.otherTypeMachine", translate);
    default:
      return lookupMachineRef(`type.${typeCode}.label`, translate);
  }
};

/**
 * Today's English composition from the reference NAMES (the pre-MACH-REF-I18N-1 algorithm), with
 * the two owner-approved deltas so English reads the same whether or not codes have arrived:
 * a non-CNC/non-line type is its name alone, and axis "Other" is skipped.
 */
export const composeEnglishLegacy = (m: ComposableMachine): string => {
  const typeName = m.type?.name || "";
  switch (typeName) {
    case "CNC Machine": {
      const axis = isOtherRef(m.axisConfiguration) ? "" : m.axisConfiguration?.name || "";
      const op = m.operationType?.name || "";
      if (!axis && !op) return "CNC machine";
      const parts: string[] = [];
      if (axis) parts.push(axis);
      parts.push("CNC");
      const opLower = op.toLowerCase();
      if (opLower === "machining") parts.push("Machining Center");
      else {
        if (op) parts.push(op);
        const hasSuffix = opLower.endsWith("machine") || opLower.endsWith("center") || opLower.endsWith("centre");
        if (!hasSuffix) parts.push("Machine");
      }
      return parts.join(" ");
    }
    case "Production line": {
      const level = m.automationLevel?.name || "";
      const op = m.operationType?.name || "";
      if (!level && !op) return typeName;
      return [level, op, "Line"].filter(Boolean).join(" ");
    }
    case "Other":
      return "Other machine";
    default:
      return typeName;
  }
};
